import { z } from 'zod';
import { Vals } from '~lib';
import { Common } from './common.model';

/**
 * @desc Markets: the regions listing and a region's live figures (UI round 2, W0.2 contract; Lane
 * B backs it with warehouse queries on the api's ClickHouse credential, no migration).
 *
 * Every warehouse figure is `number | null`: `null` means the warehouse has no value for the
 * market, which the page renders as a gap (or hides the section), never as zero. Ratios are
 * fractions (`0.124` is 12.4%) and money is whole units, as in the portal fixtures.
 *
 * Mapping from the portal fixtures (`apps/portal/src/data/types.ts`):
 * - `Region.kind` `market` splits into `country` | `state` here; `continent` is not a listing row
 *   but the `continent` facet every row carries; `rollup` is `roll-up`.
 * - `Region.code` → `isoCode`; `continentSlug` → `continent.slug`.
 * - `RegionKpis` → `Kpis` field for field, nullable where the warehouse has gaps.
 * - `AnnualGgrSet` → `AnnualGgrSet`: a point's `label` is `String(year)` and `value` is USD.
 * - `CurrencyCode` (a fixture-only union) is any ISO 4217 code here.
 */
export namespace Markets {
  export const kinds = {
    Country: 'country',
    State: 'state',
    RollUp: 'roll-up',
  } as const;
  export type Kind = Vals<typeof kinds>;
  export const ALL_KINDS = Object.values(kinds) as [Kind, ...Kind[]];

  /** @desc Illustrative this round: the warehouse does not yet hold a regulatory status. */
  export const regulatoryStatuses = {
    Regulated: 'regulated',
    Partial: 'partial',
    Unregulated: 'unregulated',
  } as const;
  export type RegulatoryStatus = Vals<typeof regulatoryStatuses>;
  export const ALL_REGULATORY_STATUSES = Object.values(regulatoryStatuses) as [
    RegulatoryStatus,
    ...RegulatoryStatus[],
  ];

  export const sortKeys = {
    Ggr: 'ggr',
    Name: 'name',
    Yoy: 'yoy',
    GgrPerAdult: 'ggr-per-adult',
    /** @desc "Recently updated" (D-6): meaningful once rows carry `updatedAt`. */
    Updated: 'updated',
  } as const;
  export type SortKey = Vals<typeof sortKeys>;
  export const ALL_SORT_KEYS = Object.values(sortKeys) as [SortKey, ...SortKey[]];

  /** @desc A page large enough for the Views overlay's "all" option over every market. */
  export const MAX_PAGE_SIZE = 500;

  /** @desc The filter sheet's GGR buckets (R8, D-4). */
  export const BUCKET_COUNT = 6;

  const figure = z.number().nullable();
  const year = z.number().int().min(1900).max(2200);

  /** @desc An amount in whole units of an ISO 4217 currency. */
  export const moneySchema = z.object({
    value: z.number(),
    currency: z.string().regex(/^[A-Z]{3}$/, 'Expected an ISO 4217 currency code'),
  });
  export type Money = z.infer<typeof moneySchema>;

  export const continentSchema = z.object({ slug: Common.slugSchema, name: z.string().min(1) });
  export type Continent = z.infer<typeof continentSchema>;

  /** @desc One row of the regions listing (cards and list views). */
  export const listingSchema = z.object({
    slug: Common.slugSchema,
    name: z.string().min(1),
    continent: continentSchema,
    kind: z.enum(ALL_KINDS),
    /** @desc ISO 3166-1 alpha-2 for a country (`GB`), ISO 3166-2 for a state (`US-NJ`); absent on roll-ups. */
    isoCode: z.string().min(2).optional(),
    /** @desc Latest full year's online GGR, USD. */
    onlineGgrUsd: figure,
    /** @desc The year `onlineGgrUsd`, `yoyGrowth` and `ggrPerAdultUsd` describe. */
    ggrYear: year.nullable(),
    yoyGrowth: figure,
    ggrPerAdultUsd: figure,
    /** @desc Operator group slugs with a licensed presence, where known. */
    operatorsPresent: z.array(Common.slugSchema).optional(),
    regulatoryStatus: z.enum(ALL_REGULATORY_STATUSES).optional(),
    /** @desc When the market's data last changed, once Lane B can say (D-6). */
    updatedAt: z.date().optional(),
  });
  export type Listing = z.infer<typeof listingSchema>;

  export const listInputSchema = z
    .object({
      /** @desc Continent slugs; a row matches any of them. */
      continents: z.array(Common.slugSchema).max(20).optional(),
      kinds: z.array(z.enum(ALL_KINDS)).optional(),
      /**
       * @desc Online GGR range, USD, inclusive. A bucket chip from `markets.filterBuckets` resolves
       * to its edges client-side; the server takes edges rather than a bucket index because the
       * edges are derived from live data and may move between the two calls.
       */
      ggrMinUsd: z.number().min(0).optional(),
      ggrMaxUsd: z.number().min(0).optional(),
      /** @desc Free-text match on the market's name. */
      q: z.string().trim().min(1).max(100).optional(),
      sort: z
        .object({ by: z.enum(ALL_SORT_KEYS), dir: z.enum(['asc', 'desc']) })
        .default({ by: sortKeys.Ggr, dir: 'desc' }),
      ...Common.pageInput(MAX_PAGE_SIZE),
    })
    .default({ sort: { by: sortKeys.Ggr, dir: 'desc' }, page: 1, limit: Common.DEFAULT_PAGE_SIZE });
  export type ListInput = z.infer<typeof listInputSchema>;

  /** @desc Rows with a `null` sort figure sort last whatever the direction. */
  export const pageSchema = Common.pageOf(listingSchema);
  export type Page = z.infer<typeof pageSchema>;

  /** @desc The region masthead's KPI cards — the fixture's `RegionKpis`. */
  export const kpisSchema = z.object({
    /** @desc The latest full year of actuals the KPIs describe. */
    year,
    onlineGgrLocal: moneySchema.nullable(),
    onlineGgrUsd: figure,
    /** @desc `forecastYear`'s forecast online GGR, USD. */
    forecastGgrUsd: figure,
    forecastYear: year.nullable(),
    ggrPerAdultUsd: figure,
    adultPopulation: figure,
    yoyGrowth: figure,
    /** @desc Active online players. */
    activePlayers: figure,
    ggrPerPlayerUsd: figure,
  });
  export type Kpis = z.infer<typeof kpisSchema>;

  /** @desc The region page's macro band — the fixture's `RegionMacro`. */
  export const macroSchema = z.object({
    /** @desc The year the macro figures describe. */
    year: year.nullable(),
    /** @desc Online GGR as a share of all GGR (online + land). */
    onlineShareOfTotal: figure,
    gdpUsd: figure,
    inflation: figure,
    gdpGrowth: figure,
  });
  export type Macro = z.infer<typeof macroSchema>;

  export const annualGgrViews = {
    ByProduct: 'by-product',
    OnlineVsLand: 'online-vs-land',
    OnshoreVsOffshore: 'onshore-vs-offshore',
  } as const;
  export type AnnualGgrView = Vals<typeof annualGgrViews>;

  export const annualPointSchema = z.object({
    year,
    /** @desc USD. */
    value: z.number(),
    /** @desc An estimate (drawn lighter, "e"). */
    forecast: z.boolean(),
  });
  export type AnnualPoint = z.infer<typeof annualPointSchema>;

  /** @desc One tab of the annual GGR chart: several segments over the same years. */
  export const annualGgrSetSchema = z.object({
    /** @desc First forecast year; `null` when the series is all actuals. */
    forecastFrom: year.nullable(),
    segments: z.array(
      z.object({
        key: z.string().min(1),
        label: z.string().min(1),
        points: z.array(annualPointSchema),
      }),
    ),
  });
  export type AnnualGgrSet = z.infer<typeof annualGgrSetSchema>;

  /** @desc The chart's tabs; a view the warehouse cannot serve for this market is absent. */
  export const annualGgrSchema = z.object({
    [annualGgrViews.ByProduct]: annualGgrSetSchema.optional(),
    [annualGgrViews.OnlineVsLand]: annualGgrSetSchema.optional(),
    [annualGgrViews.OnshoreVsOffshore]: annualGgrSetSchema.optional(),
  });
  export type AnnualGgr = z.infer<typeof annualGgrSchema>;

  /**
   * @desc A region's monthly tracking (round 3 lane 3): the last 12 months of actual online GGR,
   * oldest first, with the month's online turnover. USD; online is the headline definition (every
   * online product, onshore and offshore, lottery included). `null` where a month has no figure.
   */
  export const monthlySchema = z.object({
    months: z
      .array(
        z.object({
          month: z.string().regex(/^\d{4}-\d{2}$/, 'Expected YYYY-MM'),
          ggrUsd: figure,
          turnoverUsd: figure,
        }),
      )
      .min(1),
  });
  export type Monthly = z.infer<typeof monthlySchema>;

  export const getInputSchema = z.object({ slug: Common.slugSchema });
  export type GetInput = z.infer<typeof getInputSchema>;

  // --- Market share (licensed entity, Lane M) ----------------------------------------------------

  /** @desc A portal operator group: where a licensee's row links to. */
  export const groupRefSchema = z.object({ slug: Common.slugSchema, name: z.string().min(1) });
  export type GroupRef = z.infer<typeof groupRefSchema>;

  /**
   * @desc A licensed entity as the regulator names it (or, in the US, the brand). `group` is set
   * only where the data names a portal group (ownership chain, or the row's own brand column) —
   * never by matching the licence's name (workbook D8).
   */
  export const licenseeSchema = z.object({
    name: z.string().min(1),
    group: groupRefSchema.optional(),
  });
  export type Licensee = z.infer<typeof licenseeSchema>;

  /** @desc The licensees with online GGR in the market's latest actual year, biggest first. */
  export const operatorsPresentSchema = z.object({
    year,
    licensees: z.array(licenseeSchema).min(1),
  });
  export type OperatorsPresent = z.infer<typeof operatorsPresentSchema>;

  export const shareBases = {
    /** @desc The licensee total reconciles to the market's online GGR for the same slice (D7). */
    MarketGgr: 'market-ggr',
    /** @desc It does not (or there is no GGR to check against): shares are of the reported total. */
    ReportedLicensees: 'reported-licensees',
  } as const;
  export type ShareBasis = Vals<typeof shareBases>;

  /** @desc One licensee's share of the market. Shares are fractions; `shareChange` is in points. */
  export const shareEntrySchema = licenseeSchema.extend({
    valueLocal: z.number(),
    valueUsd: figure,
    share: z.number(),
    /** @desc 1 = the biggest named licensee; the unallocated residual has no position. */
    position: z.number().int().min(1),
    /** @desc Share now minus share the year before, as a fraction; `null` without a prior year. */
    shareChange: figure,
  });
  export type ShareEntry = z.infer<typeof shareEntrySchema>;

  /**
   * @desc The market-share bars: the top named licensees of the latest actual year, the rest
   * folded into `others`, and the regulator's unallocated residual. Everything shares one
   * denominator — the sum over every licensee row, residual included — so the parts add to 1.
   */
  export const marketShareSchema = z.object({
    year,
    currency: moneySchema.shape.currency,
    basis: z.enum([shareBases.MarketGgr, shareBases.ReportedLicensees]),
    /** @desc What the denominator covers: online, these sub-channels and master categories. */
    scope: z.object({
      subChannels: z.array(z.string().min(1)).min(1),
      masterCategories: z.array(z.string().min(1)).min(1),
    }),
    totalLocal: z.number(),
    totalUsd: figure,
    entries: z.array(shareEntrySchema).min(1),
    others: z.object({ count: z.number().int().min(1), share: z.number() }).nullable(),
    unallocatedShare: figure,
  });
  export type MarketShare = z.infer<typeof marketShareSchema>;

  /** @desc A region page's live figures. `markets.get` throws NOT_FOUND for an unknown slug. */
  export const detailSchema = z.object({
    listing: listingSchema,
    kpis: kpisSchema,
    macro: macroSchema,
    annualGgr: annualGgrSchema,
    /** @desc Absent when the warehouse has no market-share rows for the market (not "none present"). */
    operatorsPresent: operatorsPresentSchema.optional(),
    /** @desc Absent when the warehouse has no market-share rows for the market. */
    marketShare: marketShareSchema.optional(),
    /**
     * @desc Annual online Sport Betting GGR by sport, USD (`annual_ggr_by_sport`, round 3 lane 2):
     * one segment per sport, ranked by the latest actual year (ties by name). Absent when the warehouse has no by-sport rows for the market, or the table is not readable:
     * the page's "By sport" tab then says "Coming soon".
     */
    annualGgrBySport: annualGgrSetSchema.optional(),
    /**
     * @desc Monthly online GGR and turnover (migration 5, staging only for now). Absent where the
     * market has no monthly rows or the tables are not there: the page keeps its badged fixture.
     */
    monthly: monthlySchema.optional(),
  });
  export type Detail = z.infer<typeof detailSchema>;

  // --- Continents (round 3 lane 4, H2-309) -------------------------------------------------------

  /**
   * @desc A continent page's live aggregate of its member markets — the countries and roll-ups
   * whose `continent` facet it is, summed as the regions listing's continent tab sums them (a
   * state is already inside its country). Money is USD throughout (`kpis.onlineGgrLocal` is USD
   * too) and `yoyGrowth` is USD growth, since the members' currencies differ. The price index does
   * not add up across economies, so `macro.inflation` and `macro.gdpGrowth` are always `null`.
   */
  export const continentDetailSchema = z.object({
    continent: continentSchema,
    kpis: kpisSchema,
    macro: macroSchema,
    annualGgr: annualGgrSchema,
    /** @desc The markets the figures sum (countries and roll-ups), biggest online GGR first. */
    members: z.array(listingSchema).min(1),
    /** @desc The members' states (the US's, Australia's): already inside their country's figures. */
    states: z.array(listingSchema),
    /** @desc When any member's data last changed. */
    updatedAt: z.date().optional(),
  });
  export type ContinentDetail = z.infer<typeof continentDetailSchema>;

  export const continentInputSchema = z.object({ slug: Common.slugSchema });
  export type ContinentInput = z.infer<typeof continentInputSchema>;

  export const groupPresenceInputSchema = z.object({ group: Common.slugSchema });
  export type GroupPresenceInput = z.infer<typeof groupPresenceInputSchema>;

  export const groupAttributions = {
    /** @desc Through the ownership tables: the group is on the licence's ownership chain. */
    Ownership: 'ownership',
    /** @desc Through the market-share row's own brand column (US). */
    Brand: 'brand',
  } as const;
  export type GroupAttribution = Vals<typeof groupAttributions>;

  /**
   * @desc One market where licences attributed to the group report online GGR, in the market's
   * latest actual year. Through ownership, a co-owned licence counts at the group's ownership
   * fraction; `share` is the group's combined figure over the market's licensee total.
   */
  export const groupMarketShareSchema = z.object({
    market: z.object({
      slug: Common.slugSchema,
      name: z.string().min(1),
      isoCode: z.string().min(2).optional(),
    }),
    year,
    estGgrLocal: moneySchema,
    estGgrUsd: figure,
    share: z.number(),
    /** @desc Where the group's combined figure ranks among the market's named licensees. */
    position: z.number().int().min(1),
    shareChange: figure,
    basis: z.enum([shareBases.MarketGgr, shareBases.ReportedLicensees]),
    attribution: z.enum([groupAttributions.Ownership, groupAttributions.Brand]),
    /** @desc The licensee (or brand) names behind the figure. */
    licensees: z.array(z.string().min(1)).min(1),
  });
  export type GroupMarketShare = z.infer<typeof groupMarketShareSchema>;

  /**
   * @desc A group's licensed presence, biggest market first. An empty list means the warehouse
   * attributes no market-share rows to the group (today: always, until the ownership tables land),
   * so the page keeps its illustrative fixture — it never means "zero share".
   */
  export const groupPresenceSchema = z.object({
    group: Common.slugSchema,
    markets: z.array(groupMarketShareSchema),
  });
  export type GroupPresence = z.infer<typeof groupPresenceSchema>;

  /** @desc One GGR bucket, USD; `null` is an open end (the first bucket's floor, the last's ceiling). */
  export const bucketSchema = z.object({ minUsd: figure, maxUsd: figure });
  export type Bucket = z.infer<typeof bucketSchema>;

  /** @desc The six buckets, lowest first, with edges from the live distribution of `year`'s GGR. */
  export const filterBucketsSchema = z.object({
    year,
    buckets: z.array(bucketSchema).length(BUCKET_COUNT),
  });
  export type FilterBuckets = z.infer<typeof filterBucketsSchema>;
}
